import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Building2, MapPin, Globe, Briefcase, ArrowLeft, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RecruiterProfilePage() {
  const { recruiterId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/recruiters/${recruiterId}`)
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [recruiterId]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#003366]" /></div>;
  }
  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p>Recruiter not found</p>
        <Link to="/recruiters"><Button variant="outline">Back to directory</Button></Link>
      </div>
    );
  }

  const { recruiter, jobs } = data;

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      <div className="bg-gradient-to-br from-[#003366] to-[#004080] text-white py-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/recruiters" className="inline-flex items-center gap-1 text-sm text-white/70 hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4" />All recruiters
          </Link>
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center shrink-0 overflow-hidden">
              {recruiter.avatar_url
                ? <img src={recruiter.avatar_url} alt={recruiter.full_name} className="w-full h-full object-cover" />
                : <span className="text-3xl font-semibold">{(recruiter.full_name || '?').charAt(0).toUpperCase()}</span>}
            </div>
            <div>
              <h1 className="text-3xl font-bold">{recruiter.full_name}</h1>
              <div className="flex flex-wrap gap-4 mt-2 text-white/70 text-sm">
                {recruiter.company_name && <span className="flex items-center gap-1"><Building2 className="w-4 h-4" />{recruiter.company_name}</span>}
                {recruiter.location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{recruiter.location}</span>}
                {recruiter.company_website && (
                  <a href={recruiter.company_website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-white">
                    <Globe className="w-4 h-4" />{recruiter.company_website}
                  </a>
                )}
              </div>
              {(recruiter.bio || recruiter.company_description) && (
                <p className="mt-4 text-white/80 max-w-2xl">{recruiter.bio || recruiter.company_description}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Briefcase className="w-5 h-5" />Jobs posted ({jobs.length})
        </h2>
        {jobs.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground">This recruiter has no active jobs right now.</CardContent></Card>
        ) : (
          <div className="space-y-3">
            {jobs.map((job) => (
              <Card key={job.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="font-semibold truncate">{job.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {job.company_name} · {job.location} · {job.job_type?.replace('_', ' ')}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {job.remote && <Badge variant="secondary">Remote</Badge>}
                      {job.is_urgent && <Badge className="bg-red-100 text-red-700">Urgent</Badge>}
                      {job.salary_min && (
                        <Badge variant="outline">
                          {job.currency} {job.salary_min.toLocaleString()}{job.salary_max ? ` – ${job.salary_max.toLocaleString()}` : '+'}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Link to={`/jobs/${job.id}`}><Button size="sm" className="bg-[#003366] hover:bg-[#003366]/90">View job</Button></Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
